/**
 * Visual metadata for placeholder/part types: accent colors, lucide icons, cost-tag tones and the order
 * in which types (and transport sub-kinds) appear in the palette. All values are ported 1:1 from the
 * design prototype's `data.js`. Kept in one place so the board, list, palette and drawer stay in sync.
 */
import {
  Bed,
  Bus,
  Landmark,
  Package,
  Ship,
  TrainFront,
  UserRound,
  type LucideIcon,
} from 'lucide-react';

import { PlaceholderCostType, PlaceholderType, TransportKind } from '@/api/generated/schemas';

/** Type → accent color (ported from the design prototype `data.js`). */
export const TYPE_COLOR: Record<PlaceholderType, string> = {
  [PlaceholderType.Accommodation]: '#5a4a3f',
  [PlaceholderType.Transport]: '#c1742a',
  [PlaceholderType.Activities]: '#3a8b6a',
  [PlaceholderType.LocalGuides]: '#7a4a9a',
  [PlaceholderType.OtherServices]: '#7d8590',
};

const TYPE_ICON: Record<PlaceholderType, LucideIcon> = {
  [PlaceholderType.Accommodation]: Bed,
  [PlaceholderType.Transport]: Bus,
  [PlaceholderType.Activities]: Landmark,
  [PlaceholderType.LocalGuides]: UserRound,
  [PlaceholderType.OtherServices]: Package,
};

const TRANSPORT_ICON: Record<TransportKind, LucideIcon> = {
  [TransportKind.Bus]: Bus,
  [TransportKind.Boat]: Ship,
  [TransportKind.Train]: TrainFront,
};

/** Resolve the lucide icon for a part — transport blocks vary by transport kind. */
export function iconFor(type: PlaceholderType, transportKind: TransportKind | null): LucideIcon {
  if (type === PlaceholderType.Transport && transportKind) {
    return TRANSPORT_ICON[transportKind];
  }
  return TYPE_ICON[type];
}

export interface TypeIconProps {
  type: PlaceholderType;
  transportKind?: TransportKind | null;
  size?: number;
  className?: string;
}

/** Renders the type/transport lucide icon at a given size. */
export function TypeIcon({ type, transportKind = null, size = 14, className }: TypeIconProps) {
  const Icon = iconFor(type, transportKind);
  return <Icon size={size} className={className} />;
}

/** Cost type → `data-cost` tone used by the `.tp-costtag` badge. */
export const COST_TONE: Record<PlaceholderCostType, 'calc' | 'onsite' | 'none'> = {
  [PlaceholderCostType.Calculation]: 'calc',
  [PlaceholderCostType.Onsite]: 'onsite',
  [PlaceholderCostType.None]: 'none',
};

/** Order in which types (and their palette groups) appear. */
export const TYPE_ORDER: PlaceholderType[] = [
  PlaceholderType.Accommodation,
  PlaceholderType.Transport,
  PlaceholderType.Activities,
  PlaceholderType.LocalGuides,
  PlaceholderType.OtherServices,
];

export const TRANSPORT_ORDER: TransportKind[] = [
  TransportKind.Bus,
  TransportKind.Boat,
  TransportKind.Train,
];
